import { Box, Button, Typography } from "@mui/material";
import { useQuery } from "react-query";
import StatsCard from "src/components/common/StatsCard";
import defaultStyles, { StylesClasses } from "../styles";
import { getStyles } from "src/styles/theme";
import {
  getLandDashboardDetails,
  getTableDashboardDetails,
} from "src/utils/api/dashboard";
import {
  DprDashBoardTable,
  DprTableProps,
} from "src/components/common/DashBoardTable/dprDashboard";
import { exportDprTableToXlsx, formatDataForBarChart } from "src/utils/helper";
import DashboardChartSkeleton from "src/components/common/DashboardChartSkeleton";
import { useEffect, useMemo, useState } from "react";
import MultiSelect from "src/components/common/MultiSelect";
import { getStates } from "src/utils/api/roles";
import StatusBarChart from "src/components/common/StatusBarChart";
import { AxiosResponse } from "axios";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";

const TOP_HEADERS = [
  "",
  "DPR Pending",
  "DPR under DNO",
  "Query from DNO",
  "DPR under SNO",
  "Query from SNO",
  "DPR under Admin",
  "Query from Admin",
  "DPR Approved",
  "DPR Rejected",
];

const SUB_HEADERS = [
  "S.No",
  "State",
  "No",
  "Area (ha)",
  "No",
  "Area (ha)",
  "No",
  "Area (ha)",
  "No",
  "Area (ha)",
  "No",
  "Area (ha)",
  "No",
  "Area (ha)",
  "No",
  "Area (ha)",
  "No",
  "Area (ha)",
  "No",
  "Area (ha)",
];

const DprDashboard = () => {
  const [isTabLoading, setIsTabLoading] = useState(true);
  const [states, setStates] = useState<any>([]);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [filtersState, setFiltersState] = useState([]);

  const { persisted, saveFilters } = usePersistedFilters("mw_dpr");

  const [queryParams, setQueryParams] = useState<{
    projectStateIds?: string[];
    type: string;
  }>({
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    type: "dpr",
  });

  const [filterKey, setFilterKey] = useState(
    persisted?.filterKey ?? { projectStateIds: null }
  );

  const { data: dashboard, isLoading: dashboardLoading } = useQuery(
    ["dprDashboard", queryParams],
    () => getLandDashboardDetails(queryParams),
    {
      onSuccess: () => {},
      onError: () => {},
      refetchOnMount: true,
    }
  );

  const { isLoading } = useQuery(
    ["table-dpr", queryParams],
    () => getTableDashboardDetails(queryParams),
    {
      onSuccess: (res: AxiosResponse) => {
        setTableRows(
          res?.data?.stats.map((item: any, idx: number) => ({
            id: idx,
            sno: idx + 1,
            state: item.state,
            totalNumberAllDpr: item.totalNumberAllDpr,
            totalAreaAllDpr: item.totalAreaAllDpr,
            totalNumberDprUnderDno: item.totalNumberDprUnderDno,
            totalAreaDprUnderDno: item.totalAreaDprUnderDno,
            totalNumberQueryFromDno: item.totalNumberQueryFromDno,
            totalAreaQueryFromDno: item.totalAreaQueryFromDno,
            totalNumberDprUnderSno: item.totalNumberDprUnderSno,
            totalAreaDprUnderSno: item.totalAreaDprUnderSno,
            totalNumberQueryFromSno: item.totalNumberQueryFromSno,
            totalAreaQueryFromSno: item.totalAreaQueryFromSno,
            totalNumberDprUnderAdmin: item.totalNumberDprUnderAdmin,
            totalAreaDprUnderAdmin: item.totalAreaDprUnderAdmin,
            totalNumberQueryFromAdmin: item.totalNumberQueryFromAdmin,
            totalAreaQueryFromAdmin: item.totalAreaQueryFromAdmin,
            totalNumberDprApproved: item.totalNumberDprApproved,
            totalAreaDprApproved: item.totalAreaDprApproved,
            totalNumberDprRejected: item.totalNumberDprRejected,
            totalAreaDprRejected: item.totalAreaDprRejected,
          }))
        );
        setIsTabLoading(false);
      },
      onError: () => {},
      refetchOnMount: true,
    }
  );

  useQuery(["getStatesType"], () => getStates(), {
    onSuccess: (data) => {
      setStates(data.data);
    },
    refetchOnMount: true,
  });

  const formattedData = useMemo(
    () => formatDataForBarChart(dashboard?.data?.stats),
    [dashboard?.data?.stats]
  );

  const dashboardTableData: DprTableProps = {
    topHeader: TOP_HEADERS,
    header: SUB_HEADERS,
    data: tableRows,
  };

  const styles = getStyles<StylesClasses>(defaultStyles);

  useEffect(() => {
    setFiltersState(dashboard?.data?.state);
  }, [dashboard]);

  useEffect(() => {
    saveFilters({ queryParams, filterKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, filterKey]);

  useEffect(() => {
    setIsTabLoading(true);
  }, []);

  return (
    <>
      <Box {...styles("topHeader")}>
        <Box
          sx={{
            marginBottom: "5px",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {filtersState && (
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                Selected States:-
                {filtersState?.map((filterState, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      height: "46px",
                      padding: "11px",
                      width: "fit-content",
                      border: "1px solid grey",
                      borderRadius: "24px",
                      backgroundColor: "#eee",
                    }}
                  >
                    {filterState}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <MultiSelect
              options={states}
              value={filterKey.projectStateIds}
              onChange={(e: any) => {
                setFilterKey((prevFilter) => {
                  const newState = {
                    ...prevFilter,
                    projectStateIds: e.length ? e : null,
                  };
                  setQueryParams({
                    ...queryParams,
                    projectStateIds: newState?.projectStateIds,
                  });
                  return newState;
                });
              }}
            />
          </Box>
        </Box>

        {!dashboardLoading && (
          <Box
            sx={{ width: "100%", marginBottom: "20px", alignSelf: "center" }}
          >
            <StatusBarChart
              data={formattedData}
              label={"Status of DPR Under GCP"}
            />
          </Box>
        )}

        <Box {...styles("statsCardWrapper")}>
          {dashboard?.data?.stats?.map((stat: any, index: number) => (
            <StatsCard
              key={index}
              title={`${stat?.description?.toUpperCase()}`}
              value={`${stat?.number}`}
              area={stat?.totalArea}
              customStyles={{ valueCard: styles("statsCard").sx }}
            />
          ))}
        </Box>
      </Box>

      {isLoading || isTabLoading ? (
        <DashboardChartSkeleton />
      ) : (
        <Box {...styles("dashboardTableWrapper")}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h5" {...styles("heading")}>
              Status of DPR Under GCP
            </Typography>
            <Button
              variant="outlined"
              onClick={() =>
                exportDprTableToXlsx(
                  dashboardTableData.data,
                  dashboardTableData.header,
                  dashboardTableData.topHeader
                )
              }
            >
              Export
            </Button>
          </Box>
          <DprDashBoardTable {...dashboardTableData} />
        </Box>
      )}
    </>
  );
};

export default DprDashboard;
